import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"

type Severity = "ok" | "warn" | "fail"

// The CMS-constrained verdict codes the pipeline interface publishes
// (interface.statement_groups[].groups[].values.verdict) → localized label +
// severity for colouring. The backend keeps verdicts as CODES; the FE localises.
const VERDICT_META: Record<
  string,
  { labelKey: TranslationKeys; severity: Severity }
> = {
  compliant: { labelKey: "MC_DECL_VERDICT_COMPLIANT", severity: "ok" },
  compliant_with_conditions: {
    labelKey: "MC_DECL_VERDICT_COMPLIANT_WITH_CONDITIONS",
    severity: "warn",
  },
  non_compliant: {
    labelKey: "MC_DECL_VERDICT_NON_COMPLIANT",
    severity: "fail",
  },
  pending: { labelKey: "MC_DECL_VERDICT_PENDING", severity: "warn" },
  unknown: { labelKey: "MC_DECL_VERDICT_UNKNOWN", severity: "warn" },
}

/**
 * The meta for a code the table actually knows, or null. Callers that read the
 * MASTER-DATA status vocabulary use this rather than `verdictMeta`: that
 * vocabulary still holds legacy import codes ('exempt', 'not_applicable') which
 * `verdictMeta`'s fallback would relabel "unknown" — destroying the very
 * information the row was showing. Callers on canonical verdicts use
 * `verdictMeta` and its fallback.
 */
export function knownVerdictMeta(
  verdict: string,
): { labelKey: TranslationKeys; severity: Severity } | null {
  return VERDICT_META[verdict] ?? null
}

export function verdictMeta(verdict: string): {
  labelKey: TranslationKeys
  severity: Severity
} {
  return (
    VERDICT_META[verdict] ?? {
      labelKey: "MC_DECL_VERDICT_UNKNOWN",
      severity: "warn",
    }
  )
}
